import { useTranslation } from "react-i18next"

import { useAgentState } from "@/agent-state"
import { useDisplayAgentStatus } from "@/agent-state/agent-status-hooks"
import { isHandlingV2Task } from "@/agent-state/state-helpers"
import { useConnectActionsHook } from "@/hooks/connectActions"
import { useContactDataHook } from "@/hooks/contactData"
import { useToast } from "@/hooks/toastHook"
import { useCallPanelLogger } from "@/hooks/useLogger"
import { useSendCallReport } from "@/hooks/useSendCallReport"
import * as connectPanelValue from "@/pages/CustomConnectPanel/value"

interface GoOfflineHookType {
  goOffline: () => Promise<void>
  isLoading: boolean
}

const useGoOfflineHook = (
  currentCase: connectPanelValue.Decoder.CaseAndActivityFeeds | null,
): GoOfflineHookType => {
  const { send, state } = useAgentState()
  const { setAgentStatus } = useDisplayAgentStatus()

  const log = useCallPanelLogger()
  const { error: toastError } = useToast()
  const { t } = useTranslation()

  const { closeContact, endTask } = useConnectActionsHook()

  const { isTask, isVoiceCallWithError, isVoiceContact } = useContactDataHook()

  const sendCallReportMutation = useSendCallReport({
    currentCase,
  })

  const sendCallReport = async () => {
    try {
      await sendCallReportMutation.mutateAsync({
        callReportType: "automatic-going-offline",
      })
    } catch {
      // Already logged by mutation onError; avoid double report to Sentry
      return
    }
  }

  const sendSkipCallReport = async (): Promise<void> => {
    try {
      await sendCallReport()
      if (await setAgentStatus("offline", { enqueueNextState: true })) {
        send({ type: "GO_OFFLINE" })
      }
    } catch (error) {
      log.error(error)
    }
  }

  const stopCurrentTask = async () => {
    try {
      await endTask()
    } catch (error) {
      log.error(error)
    }
  }

  const goOffline = async (): Promise<void> => {
    try {
      if (isVoiceCallWithError) {
        await closeContact()
        if (await setAgentStatus("offline")) {
          send({ type: "GO_OFFLINE" })
        }
      } else if (isVoiceContact) {
        return toastError(t("global.modals.goOffline.callError"))
      } else if (isTask || isHandlingV2Task(state.context)) {
        await sendSkipCallReport()
        await stopCurrentTask()
      }
    } catch (error) {
      log.error(error)
    }
  }

  return { goOffline, isLoading: sendCallReportMutation.isPending }
}

export { useGoOfflineHook }
